import { Box, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getDiamondCasinoUserPLSummaryAPI,
  getDiamondCasinoTransactionsAPI,
  getDiamondCasinoRoundHistoryAPI,
  getDiamondCasinoProvidersAPI,
  getDiamondCasinoSummaryAPI,
  getDiamondCasinoProviderPLSummaryAPI,
} from "../../service/AuthService";
import moment from "moment";
import "./reports-mobile.css";
import "./CasinoReport.css";

const TABS = [
  { key: "casino-pl", label: "Casino P&L" },
  { key: "user-pl", label: "User P&L" },
  { key: "transactions", label: "Transactions" },
  { key: "round-history", label: "Round History" },
];

const formatNumber = (num) => {
  if (num === undefined || num === null) return "0";
  return Number(num).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatDateArray = (val) => {
  if (!val) return "-";
  if (Array.isArray(val) && val.length >= 5) {
    const [year, month, day, hour, minute, second] = val;
    return moment({ year, month: month - 1, day, hour, minute, second: second || 0 }).format("DD/MM/YYYY HH:mm:ss");
  }
  const m = moment(val);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : "-";
};

const formatLastPlayed = (lp) => {
  if (!lp) return "-";
  if (Array.isArray(lp) && lp.length >= 5) {
    const [year, month, day, hour, minute] = lp;
    return moment({ year, month: month - 1, day, hour, minute }).format("DD/MM/YYYY HH:mm");
  }
  const m = moment(lp);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "-";
};

export default function CasinoReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramUser = searchParams.get("userName") || "";
  const paramTab = searchParams.get("tab") || "";

  const getInitialTab = () => {
    const match = TABS.find((t) => t.key === paramTab);
    return match ? match.key : "casino-pl";
  };

  // Shared filter state
  const [from, setFrom] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [providerCode, setProviderCode] = useState("");
  const [userName, setUserName] = useState(paramUser);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [providers, setProviders] = useState([]);

  // Casino P&L state
  const [cpSummary, setCpSummary] = useState(null);
  const [cpProviderData, setCpProviderData] = useState([]);
  const [cpLoading, setCpLoading] = useState(false);
  const [cpSearched, setCpSearched] = useState(false);
  const [cpExpandedRows, setCpExpandedRows] = useState(new Set());

  // User P&L state
  const [plData, setPlData] = useState([]);
  const [plLoading, setPlLoading] = useState(false);
  const [plSearched, setPlSearched] = useState(false);
  const [plSearchText, setPlSearchText] = useState("");
  const [plPageSize, setPlPageSize] = useState(25);
  const [plCurrentPage, setPlCurrentPage] = useState(0);
  const [plExpandedRows, setPlExpandedRows] = useState(new Set());

  // Transactions state
  const [txData, setTxData] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txSearched, setTxSearched] = useState(false);
  const [txPage, setTxPage] = useState(0);
  const [txPageSize, setTxPageSize] = useState(25);
  const [txHasMore, setTxHasMore] = useState(false);
  const [txType, setTxType] = useState("ALL");
  const [txExpandedRows, setTxExpandedRows] = useState(new Set());

  // Round History state
  const [rhData, setRhData] = useState([]);
  const [rhLoading, setRhLoading] = useState(false);
  const [rhSearched, setRhSearched] = useState(false);
  const [rhPage, setRhPage] = useState(0);
  const [rhPageSize, setRhPageSize] = useState(25);
  const [rhHasMore, setRhHasMore] = useState(false);
  const [rhGameCode, setRhGameCode] = useState("");
  const [rhExpandedRows, setRhExpandedRows] = useState(new Set());

  // Fetch providers on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await getDiamondCasinoProvidersAPI();
        const list = res?.response || res?.data || [];
        setProviders(Array.isArray(list) ? list : []);
      } catch {
        setProviders([]);
      }
    })();
  }, []);

  // --- Data fetching ---
  const fetchCasinoPL = useCallback(async () => {
    setCpLoading(true);
    setCpSearched(true);
    setCpExpandedRows(new Set());
    try {
      const payload = {};
      if (from) payload.from = moment(from).startOf("day").toISOString();
      if (to) payload.to = moment(to).endOf("day").toISOString();
      if (providerCode) payload.providerCode = providerCode;
      const [summaryRes, providerRes] = await Promise.all([
        getDiamondCasinoSummaryAPI(payload),
        getDiamondCasinoProviderPLSummaryAPI(payload),
      ]);
      // summary endpoint returns { response: { totals: {...} } }
      const summaryData = summaryRes?.response || summaryRes?.data || {};
      setCpSummary(summaryData.totals || summaryData);
      // provider-pl-summary returns { response: { providers: [...] } }
      const providerData = providerRes?.response || providerRes?.data || {};
      const provList = providerData.providers || providerData || [];
      setCpProviderData(Array.isArray(provList) ? provList : []);
    } catch {
      setCpSummary(null);
      setCpProviderData([]);
    }
    setCpLoading(false);
  }, [from, to, providerCode]);

  const fetchUserPL = useCallback(async () => {
    setPlLoading(true);
    setPlSearched(true);
    setPlExpandedRows(new Set());
    try {
      const payload = {};
      if (from) payload.from = moment(from).startOf("day").toISOString();
      if (to) payload.to = moment(to).endOf("day").toISOString();
      if (userName.trim()) payload.userName = userName.trim();
      const res = await getDiamondCasinoUserPLSummaryAPI(payload);
      const list = res?.response || res?.data || [];
      setPlData(
        Array.isArray(list)
          ? list.filter((row) => (row.totalStake || 0) !== 0 || (row.totalPayout || 0) !== 0 || (row.netPL || 0) !== 0)
          : []
      );
    } catch {
      setPlData([]);
    }
    setPlLoading(false);
    setPlCurrentPage(0);
  }, [from, to, userName]);

  const fetchTransactions = useCallback(async (pageNum = 0) => {
    setTxLoading(true);
    setTxSearched(true);
    setTxExpandedRows(new Set());
    try {
      const payload = { page: pageNum, size: txPageSize };
      if (from) payload.from = moment(from).startOf("day").toISOString();
      if (to) payload.to = moment(to).endOf("day").toISOString();
      if (userName.trim()) payload.userName = userName.trim();
      if (providerCode) payload.providerCode = providerCode;
      if (txType !== "ALL") payload.txnType = txType;
      const res = await getDiamondCasinoTransactionsAPI(payload);
      const content = res?.response?.content || res?.data?.content || res?.response || res?.data || [];
      const list = Array.isArray(content) ? content : [];
      setTxData(list);
      setTxHasMore(list.length >= txPageSize);
      setTxPage(pageNum);
    } catch {
      setTxData([]);
      setTxHasMore(false);
    }
    setTxLoading(false);
  }, [from, to, userName, providerCode, txType, txPageSize]);

  const fetchRoundHistory = useCallback(async (pageNum = 0) => {
    setRhLoading(true);
    setRhSearched(true);
    setRhExpandedRows(new Set());
    try {
      const payload = { page: pageNum, size: rhPageSize };
      if (from) payload.from = moment(from).startOf("day").toISOString();
      if (to) payload.to = moment(to).endOf("day").toISOString();
      if (providerCode) payload.providerCode = providerCode;
      if (rhGameCode.trim()) payload.gameCode = rhGameCode.trim();
      const res = await getDiamondCasinoRoundHistoryAPI(payload);
      const content = res?.response?.content || res?.data?.content || res?.response || res?.data || [];
      const list = Array.isArray(content) ? content : [];
      setRhData(list);
      setRhHasMore(list.length >= rhPageSize);
      setRhPage(pageNum);
    } catch {
      setRhData([]);
      setRhHasMore(false);
    }
    setRhLoading(false);
  }, [from, to, providerCode, rhGameCode, rhPageSize]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeTab === "casino-pl") fetchCasinoPL();
    else if (activeTab === "user-pl") fetchUserPL();
    else if (activeTab === "transactions") fetchTransactions(0);
    else if (activeTab === "round-history") fetchRoundHistory(0);
  };

  // Auto-fetch on mount for the active tab
  useEffect(() => {
    if (activeTab === "casino-pl") fetchCasinoPL();
    else if (activeTab === "user-pl") fetchUserPL();
    else if (activeTab === "transactions") fetchTransactions(0);
    else if (activeTab === "round-history") fetchRoundHistory(0);
  }, []);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    // Auto-fetch if this tab hasn't loaded yet
    const notSearched =
      (tabKey === "casino-pl" && !cpSearched) ||
      (tabKey === "user-pl" && !plSearched) ||
      (tabKey === "transactions" && !txSearched) ||
      (tabKey === "round-history" && !rhSearched);
    if (notSearched) {
      setTimeout(() => {
        if (tabKey === "casino-pl") fetchCasinoPL();
        else if (tabKey === "user-pl") fetchUserPL();
        else if (tabKey === "transactions") fetchTransactions(0);
        else if (tabKey === "round-history") fetchRoundHistory(0);
      }, 0);
    }
  };

  // Cross-tab: clicking username in User P&L switches to Transactions
  const handleUsernamePLClick = (uname) => {
    setUserName(uname);
    setActiveTab("transactions");
    // Defer fetch to next tick so state is updated
    setTimeout(() => {
      setTxLoading(true);
      setTxSearched(true);
      setTxExpandedRows(new Set());
      (async () => {
        try {
          const payload = {
            from: new Date(from).toISOString(),
            to: new Date(to).toISOString(),
            page: 0,
            size: txPageSize,
          };
          payload.userName = uname;
          if (providerCode) payload.providerCode = providerCode;
          if (txType !== "ALL") payload.txnType = txType;
          const res = await getDiamondCasinoTransactionsAPI(payload);
          const content = res?.response?.content || res?.data?.content || res?.response || res?.data || [];
          const list = Array.isArray(content) ? content : [];
          setTxData(list);
          setTxHasMore(list.length >= txPageSize);
          setTxPage(0);
        } catch {
          setTxData([]);
          setTxHasMore(false);
        }
        setTxLoading(false);
      })();
    }, 0);
  };

  const isLoading = activeTab === "casino-pl" ? cpLoading : activeTab === "user-pl" ? plLoading : activeTab === "transactions" ? txLoading : rhLoading;

  // --- Toggle helpers ---
  const toggleCpRow = (idx) => {
    setCpExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };
  const togglePlRow = (idx) => {
    setPlExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };
  const toggleTxRow = (idx) => {
    setTxExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };
  const toggleRhRow = (idx) => {
    setRhExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  // --- User P&L tab ---
  const plFiltered = useMemo(() => {
    if (!plSearchText) return plData;
    const q = plSearchText.toLowerCase();
    return plData.filter((row) => (row.userName || row.username || "").toLowerCase().includes(q));
  }, [plData, plSearchText]);

  const plTotalPages = Math.ceil(plFiltered.length / plPageSize);
  const plStartIdx = plCurrentPage * plPageSize;
  const plDisplay = plFiltered.slice(plStartIdx, plStartIdx + plPageSize);

  const getNetPL = (row) => row.netPL != null ? row.netPL : (row.totalStake || 0) - (row.totalPayout || 0);
  const getUsername = (row) => row.userName || row.username || "-";
  const getTypeLabel = (row) => {
    const t = (row.txnType || row.type || "").toUpperCase();
    if (t === "DEBIT") return "Withdrawal";
    if (t === "CREDIT") return "Deposit";
    return row.txnType || row.type || "-";
  };

  // --- Casino P&L tab ---
  const renderCasinoPLTable = () => {
    const summaryCards = cpSummary ? [
      { label: "Total Stake", value: formatNumber(cpSummary.totalStake), color: "#1a1a2e" },
      { label: "Total Payout", value: formatNumber(cpSummary.totalPayout), color: "#1a1a2e" },
      { label: "Net P&L", value: formatNumber(cpSummary.netPL != null ? cpSummary.netPL : (cpSummary.totalStake || 0) - (cpSummary.totalPayout || 0)), color: (cpSummary.netPL != null ? cpSummary.netPL : (cpSummary.totalStake || 0) - (cpSummary.totalPayout || 0)) >= 0 ? "#198754" : "#dc3545" },
      { label: "Total Rounds", value: cpSummary.totalRounds || cpSummary.distinctRounds || 0, color: "#1a1a2e" },
      { label: "Total Transactions", value: cpSummary.totalTransactions || cpSummary.txnCount || 0, color: "#1a1a2e" },
      { label: "Active Players", value: cpSummary.activePlayers || cpSummary.activeUsers || 0, color: "#1a1a2e" },
    ] : [];

    return (
      <>
        {/* Overall Summary Cards */}
        {cpSearched && !cpLoading && cpSummary && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", padding: "16px 0" }}>
            {summaryCards.map((card) => (
              <div
                key={card.label}
                style={{
                  flex: "1 1 150px",
                  minWidth: "140px",
                  background: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "14px 16px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}
              >
                <div style={{ fontSize: "12px", color: "#777", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "4px" }}>
                  {card.label}
                </div>
                <div style={{ fontSize: "20px", fontWeight: 700, color: card.color }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Provider-wise P&L Table */}
        <div className="dataTables_wrapper no-footer">
          {cpSearched && !cpLoading && cpProviderData.length > 0 && (
            <h6 style={{ fontWeight: 700, color: "#1a1a2e", margin: "8px 0 12px", fontSize: "15px" }}>
              Provider-wise Breakdown
            </h6>
          )}

          <table
            className="table table-bordered datatable dataTable no-footer report-table casino-round-table"
            style={{ width: "100%" }}
            role="grid"
          >
            <thead>
              <tr role="row" style={{ backgroundColor: "#C9C9C9" }}>
                <th>S.No.</th>
                <th>Provider</th>
                <th>Total Stake</th>
                <th>Total Payout</th>
                <th>Net P&L</th>
                <th>Rounds</th>
                <th>Transactions</th>
                <th>Players</th>
              </tr>
            </thead>
            <tbody>
              {!cpSearched ? (
                <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                  <td colSpan={8} className="dataTables_empty">Select date range and click Submit</td>
                </tr>
              ) : cpLoading ? (
                <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                  <td colSpan={8} className="dataTables_empty">Loading...</td>
                </tr>
              ) : cpProviderData.length === 0 ? (
                <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                  <td colSpan={8} className="dataTables_empty">No data available in table</td>
                </tr>
              ) : (
                cpProviderData.map((row, index) => {
                  const netPL = row.netPL != null ? row.netPL : (row.totalStake || 0) - (row.totalPayout || 0);
                  const isExpanded = cpExpandedRows.has(index);
                  return (
                    <tr
                      key={row.providerCode || row.provider || index}
                      role="row"
                      className={isExpanded ? "mobile-expanded" : ""}
                      style={{ backgroundColor: "#FFFFFF" }}
                    >
                      <td className="mobile-sr-no">{index + 1}</td>
                      <td className="mobile-primary-cell" data-label="Provider">
                        <span style={{ fontWeight: 600 }}>{row.providerName || row.providerCode || "-"}</span>
                        <button
                          className="casino-card-chevron"
                          onClick={() => toggleCpRow(index)}
                          type="button"
                          aria-label={isExpanded ? "Hide details" : "Show details"}
                        >
                          <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`} />
                        </button>
                      </td>
                      <td className="mobile-detail" data-label="Total Stake">{formatNumber(row.totalStake)}</td>
                      <td className="mobile-detail" data-label="Total Payout">{formatNumber(row.totalPayout)}</td>
                      <td
                        className="mobile-detail"
                        data-label="Net P&L"
                        style={{ color: netPL >= 0 ? "#198754" : "#dc3545", fontWeight: 600 }}
                      >
                        {formatNumber(netPL)}
                      </td>
                      <td className="mobile-detail" data-label="Rounds">{row.totalRounds || row.distinctRounds || 0}</td>
                      <td className="mobile-detail" data-label="Transactions">{row.totalTransactions || row.txnCount || 0}</td>
                      <td className="mobile-detail" data-label="Players">{row.activePlayers || row.activeUsers || row.participantCount || 0}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderUserPLTable = () => (
    <>
      <div className="dataTables_wrapper no-footer">
        <div className="casino-report-secondary-filters">
          <div className="dataTables_length">
            <label>
              Show{" "}
              <select
                value={plPageSize}
                onChange={(e) => {
                  setPlPageSize(Number(e.target.value));
                  setPlCurrentPage(0);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>{" "}
              entries
            </label>
          </div>
          <div className="dataTables_filter">
            <label>
              Search:
              <input
                type="search"
                placeholder="Filter by username"
                value={plSearchText}
                onChange={(e) => {
                  setPlSearchText(e.target.value);
                  setPlCurrentPage(0);
                }}
              />
            </label>
          </div>
        </div>

        <table
          className="table table-bordered datatable dataTable no-footer report-table casino-round-table"
          style={{ width: "100%" }}
          role="grid"
        >
          <thead>
            <tr role="row" style={{ backgroundColor: "#C9C9C9" }}>
              <th>S.No.</th>
              <th>Username</th>
              <th>Total Stake</th>
              <th>Total Payout</th>
              <th>Net P&L</th>
              <th>Txn Count</th>
              <th>Rounds Played</th>
              <th>Last Played</th>
            </tr>
          </thead>
          <tbody>
            {!plSearched ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={8} className="dataTables_empty">Select date range and click Submit</td>
              </tr>
            ) : plLoading ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={8} className="dataTables_empty">Loading...</td>
              </tr>
            ) : plDisplay.length === 0 ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={8} className="dataTables_empty">No data available in table</td>
              </tr>
            ) : (
              plDisplay.map((row, index) => {
                const netPL = getNetPL(row);
                const uname = getUsername(row);
                const isExpanded = plExpandedRows.has(index);
                return (
                  <tr
                    key={uname + index}
                    role="row"
                    className={isExpanded ? "mobile-expanded" : ""}
                    style={{ backgroundColor: "#FFFFFF" }}
                  >
                    <td className="mobile-sr-no">{plStartIdx + index + 1}</td>
                    <td className="mobile-primary-cell" data-label="Username">
                      <span
                        style={{ cursor: "pointer", color: "#0d6efd", textDecoration: "underline" }}
                        onClick={() => handleUsernamePLClick(uname)}
                      >
                        {uname}
                      </span>
                      <button
                        className="casino-card-chevron"
                        onClick={() => togglePlRow(index)}
                        type="button"
                        aria-label={isExpanded ? "Hide details" : "Show details"}
                      >
                        <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`} />
                      </button>
                    </td>
                    <td className="mobile-detail" data-label="Total Stake">{formatNumber(row.totalStake)}</td>
                    <td className="mobile-detail" data-label="Total Payout">{formatNumber(row.totalPayout)}</td>
                    <td
                      className="mobile-detail"
                      data-label="Net P&L"
                      style={{ color: netPL >= 0 ? "#198754" : "#dc3545", fontWeight: 600 }}
                    >
                      {formatNumber(netPL)}
                    </td>
                    <td className="mobile-detail" data-label="Txn Count">{row.txnCount || 0}</td>
                    <td className="mobile-detail" data-label="Rounds Played">{row.distinctRounds || 0}</td>
                    <td className="mobile-detail" data-label="Last Played">{formatLastPlayed(row.lastPlayed)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {plFiltered.length > 0 && (
          <div
            className="dataTables_paginate paging_simple_numbers"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", flexWrap: "wrap", gap: "8px" }}
          >
            <span style={{ fontSize: "13px", color: "#333" }}>
              Showing {plStartIdx + 1} to {Math.min(plStartIdx + plPageSize, plFiltered.length)} of {plFiltered.length} entries
            </span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                className="btn btn-diamond"
                disabled={plCurrentPage === 0}
                onClick={() => setPlCurrentPage((p) => p - 1)}
                style={{
                  background: "linear-gradient(to bottom, #f0c454, #d4a017)",
                  color: "#1a1a2e",
                  fontWeight: "600",
                  border: "none",
                  opacity: plCurrentPage === 0 ? 0.5 : 1,
                }}
              >
                Previous
              </button>
              <span style={{ padding: "6px 12px", fontSize: "13px", fontWeight: "600" }}>
                Page {plCurrentPage + 1} of {plTotalPages}
              </span>
              <button
                className="btn btn-diamond"
                disabled={plCurrentPage >= plTotalPages - 1}
                onClick={() => setPlCurrentPage((p) => p + 1)}
                style={{
                  background: "linear-gradient(to bottom, #f0c454, #d4a017)",
                  color: "#1a1a2e",
                  fontWeight: "600",
                  border: "none",
                  opacity: plCurrentPage >= plTotalPages - 1 ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // --- Transactions tab ---
  const renderTransactionsTable = () => (
    <>
      <div className="dataTables_wrapper no-footer">
        <div className="casino-report-secondary-filters">
          <div className="dataTables_length">
            <label>
              Show{" "}
              <select
                value={txPageSize}
                onChange={(e) => setTxPageSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>{" "}
              entries
            </label>
          </div>
          <div className="dataTables_filter">
            <label>
              Txn Type:
              <select
                className="form-control"
                value={txType}
                onChange={(e) => setTxType(e.target.value)}
                style={{ display: "inline-block", width: "auto", marginLeft: "6px" }}
              >
                <option value="ALL">All</option>
                <option value="DEBIT">Withdrawal</option>
                <option value="CREDIT">Deposit</option>
              </select>
            </label>
          </div>
        </div>

        <table
          className="table table-bordered datatable dataTable no-footer report-table casino-round-table"
          style={{ width: "100%" }}
          role="grid"
        >
          <thead>
            <tr role="row" style={{ backgroundColor: "#C9C9C9" }}>
              <th>S.No.</th>
              <th>Txn ID</th>
              <th>Username</th>
              <th>Provider</th>
              <th>Game</th>
              <th>Round ID</th>
              <th>Type</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Bal Before</th>
              <th>Bal After</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {!txSearched ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={12} className="dataTables_empty">Select filters and click Submit</td>
              </tr>
            ) : txLoading ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={12} className="dataTables_empty">Loading...</td>
              </tr>
            ) : txData.length === 0 ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={12} className="dataTables_empty">No data available in table</td>
              </tr>
            ) : (
              txData.map((row, index) => {
                const isDebit = (row.txnType || row.type || "").toUpperCase() === "DEBIT";
                const rowBg = isDebit ? "#BBE3FF" : "#FFCCDF";
                const isExpanded = txExpandedRows.has(index);
                return (
                  <tr
                    key={row.transactionId || row.id || index}
                    role="row"
                    className={isExpanded ? "mobile-expanded" : ""}
                    style={{ backgroundColor: rowBg, color: "#000" }}
                  >
                    <td className="mobile-sr-no">{txPage * txPageSize + index + 1}</td>
                    <td className="mobile-primary-cell" data-label="Txn ID">
                      <span>{row.transactionId || row.txnId || row.id || "-"}</span>
                      <span className="mobile-primary-right">{row.username || row.userName || ""}</span>
                      <button
                        className="casino-card-chevron"
                        onClick={() => toggleTxRow(index)}
                        type="button"
                        aria-label={isExpanded ? "Hide details" : "Show details"}
                      >
                        <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`} />
                      </button>
                    </td>
                    <td className="mobile-detail" data-label="Username">{row.username || row.userName || "-"}</td>
                    <td className="mobile-detail" data-label="Provider">{row.providerName || row.providerCode || "-"}</td>
                    <td className="mobile-detail" data-label="Game">{row.gameCode || row.game || "-"}</td>
                    <td className="mobile-detail" data-label="Round ID" style={{ wordBreak: "break-all" }}>{row.providerRoundId || row.roundId || "-"}</td>
                    <td className="mobile-detail" data-label="Type">
                      <span style={{ fontWeight: 600, color: isDebit ? "#0d6efd" : "#dc3545" }}>
                        {getTypeLabel(row)}
                      </span>
                    </td>
                    <td className="mobile-detail" data-label="Description">{row.description || row.desc || "-"}</td>
                    <td className="mobile-detail" data-label="Amount" style={{ fontWeight: 600 }}>{formatNumber(row.amount)}</td>
                    <td className="mobile-detail" data-label="Bal Before">{formatNumber(row.balanceBefore ?? row.balBefore)}</td>
                    <td className="mobile-detail" data-label="Bal After">{formatNumber(row.balanceAfter ?? row.balAfter)}</td>
                    <td className="mobile-detail" data-label="Date">{formatDateArray(row.createdAt || row.date)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {txSearched && !txLoading && (
          <div
            className="dataTables_paginate paging_simple_numbers"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", flexWrap: "wrap", gap: "8px" }}
          >
            <span style={{ fontSize: "13px", color: "#333" }}>
              Page {txPage + 1} {txData.length > 0 ? `(${txData.length} records)` : ""}
            </span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                className="btn btn-diamond"
                disabled={txPage === 0}
                onClick={() => fetchTransactions(txPage - 1)}
                style={{
                  background: "linear-gradient(to bottom, #f0c454, #d4a017)",
                  color: "#1a1a2e",
                  fontWeight: "600",
                  border: "none",
                  opacity: txPage === 0 ? 0.5 : 1,
                }}
              >
                Previous
              </button>
              <button
                className="btn btn-diamond"
                disabled={!txHasMore}
                onClick={() => fetchTransactions(txPage + 1)}
                style={{
                  background: "linear-gradient(to bottom, #f0c454, #d4a017)",
                  color: "#1a1a2e",
                  fontWeight: "600",
                  border: "none",
                  opacity: !txHasMore ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // --- Round History tab ---
  const parseRoundDate = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length < 6) return "-";
    const [year, month, day, hour, minute, second] = arr;
    return moment([year, month - 1, day, hour, minute, second]).format("DD/MM/YYYY HH:mm:ss");
  };

  const renderRoundHistoryTable = () => (
    <>
      <div className="dataTables_wrapper no-footer">
        <div className="casino-report-secondary-filters">
          <div className="dataTables_length">
            <label>
              Show{" "}
              <select
                value={rhPageSize}
                onChange={(e) => setRhPageSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>{" "}
              entries
            </label>
          </div>
          <div className="dataTables_filter">
            <label>
              Game Code:
              <input
                type="text"
                placeholder="Filter by game code"
                value={rhGameCode}
                onChange={(e) => setRhGameCode(e.target.value)}
                style={{ marginLeft: "6px" }}
              />
            </label>
          </div>
        </div>

        <table
          className="table table-bordered datatable dataTable no-footer report-table casino-round-table"
          style={{ width: "100%" }}
          role="grid"
        >
          <thead>
            <tr role="row" style={{ backgroundColor: "#C9C9C9" }}>
              <th>S.No.</th>
              <th>Round ID</th>
              <th>Provider</th>
              <th>Game Code</th>
              <th>Total Stake</th>
              <th>Total Payout</th>
              <th>Net P&L</th>
              <th>Players</th>
              <th>Txns</th>
              <th>Round Start</th>
              <th>Round End</th>
            </tr>
          </thead>
          <tbody>
            {!rhSearched ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={11} className="dataTables_empty">Select date range and click Submit</td>
              </tr>
            ) : rhLoading ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={11} className="dataTables_empty">Loading...</td>
              </tr>
            ) : rhData.length === 0 ? (
              <tr style={{ backgroundColor: "#FFFFFF", textAlign: "center" }}>
                <td colSpan={11} className="dataTables_empty">No data available in table</td>
              </tr>
            ) : (
              rhData.map((row, index) => {
                const netPL = row.netPL != null ? row.netPL : (row.totalStake || 0) - (row.totalPayout || 0);
                const isExpanded = rhExpandedRows.has(index);
                return (
                  <tr
                    key={row.providerRoundId || index}
                    role="row"
                    className={isExpanded ? "mobile-expanded" : ""}
                    style={{ backgroundColor: "#FFFFFF" }}
                  >
                    <td className="mobile-sr-no">{rhPage * rhPageSize + index + 1}</td>
                    <td
                      className="mobile-primary-cell"
                      data-label="Round ID"
                      style={{ fontSize: "12px", wordBreak: "break-all" }}
                    >
                      {row.providerRoundId || "-"}
                      <button
                        className="casino-card-chevron"
                        onClick={() => toggleRhRow(index)}
                        type="button"
                        aria-label={isExpanded ? "Hide details" : "Show details"}
                      >
                        <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`} />
                      </button>
                    </td>
                    <td className="mobile-detail" data-label="Provider">{row.providerName || row.providerCode || "-"}</td>
                    <td className="mobile-detail" data-label="Game Code">{row.gameCode || "-"}</td>
                    <td className="mobile-detail" data-label="Total Stake">{formatNumber(row.totalStake)}</td>
                    <td className="mobile-detail" data-label="Total Payout">{formatNumber(row.totalPayout)}</td>
                    <td
                      className="mobile-detail"
                      data-label="Net P&L"
                      style={{ color: netPL >= 0 ? "#198754" : "#dc3545", fontWeight: 600 }}
                    >
                      {formatNumber(netPL)}
                    </td>
                    <td className="mobile-detail" data-label="Players">{row.participantCount || 0}</td>
                    <td className="mobile-detail" data-label="Transactions">{row.transactionCount || 0}</td>
                    <td className="mobile-detail" data-label="Round Start">{parseRoundDate(row.roundStart)}</td>
                    <td className="mobile-detail" data-label="Round End">{parseRoundDate(row.roundEnd)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {rhSearched && !rhLoading && (
          <div
            className="dataTables_paginate paging_simple_numbers"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", flexWrap: "wrap", gap: "8px" }}
          >
            <span style={{ fontSize: "13px", color: "#333" }}>
              Page {rhPage + 1} {rhData.length > 0 ? `(${rhData.length} records)` : ""}
            </span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                className="btn btn-diamond"
                disabled={rhPage === 0}
                onClick={() => fetchRoundHistory(rhPage - 1)}
                style={{
                  background: "linear-gradient(to bottom, #f0c454, #d4a017)",
                  color: "#1a1a2e",
                  fontWeight: "600",
                  border: "none",
                  opacity: rhPage === 0 ? 0.5 : 1,
                }}
              >
                Previous
              </button>
              <button
                className="btn btn-diamond"
                disabled={!rhHasMore}
                onClick={() => fetchRoundHistory(rhPage + 1)}
                style={{
                  background: "linear-gradient(to bottom, #f0c454, #d4a017)",
                  color: "#1a1a2e",
                  fontWeight: "600",
                  border: "none",
                  opacity: !rhHasMore ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <Box className="col-md-12 report-main-content m-t-5">
      <Box
        bg="#C9C9C9"
        p="2"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        w="100%"
        fontWeight="bold"
        color="#1a1a2e"
      >
        <Text fontSize="xl">Casino Report</Text>
      </Box>

      <div className="main">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12 main-container">
              <div className="listing-grid">
                {/* Filters — conditional per tab */}
                <div className="detail-row">
                  <form method="post" id="form-search" onSubmit={handleSubmit}>
                    <div className="m-t-20">
                      <div className="row">
                        <div className="col-md-2">
                          <div className="form-group">
                            <label>From Date</label>
                            <input
                              className="form-control"
                              type="date"
                              value={from}
                              onChange={(e) => setFrom(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-2">
                          <div className="form-group">
                            <label>To Date</label>
                            <input
                              className="form-control"
                              type="date"
                              value={to}
                              onChange={(e) => setTo(e.target.value)}
                            />
                          </div>
                        </div>
                        {(activeTab === "casino-pl" || activeTab === "transactions" || activeTab === "round-history") && (
                          <div className="col-md-2">
                            <div className="form-group">
                              <label>Provider</label>
                              <select
                                className="form-control"
                                value={providerCode}
                                onChange={(e) => setProviderCode(e.target.value)}
                              >
                                <option value="">All Providers</option>
                                {providers.map((p) => (
                                  <option key={p.providerCode || p.code || p} value={p.providerCode || p.code || p}>
                                    {p.providerName || p.name || p.providerCode || p.code || p}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                        {(activeTab === "user-pl" || activeTab === "transactions") && (
                          <div className="col-md-2">
                            <div className="form-group">
                              <label>Player Name</label>
                              <input
                                className="form-control"
                                type="text"
                                placeholder="Optional"
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                        <div className="col-md-2 casino-report-submit-col">
                          <div className="form-group" style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", height: "100%" }}>
                            <button
                              type="submit"
                              className="btn btn-diamond"
                              disabled={isLoading}
                              style={{
                                background: "linear-gradient(to bottom, #f0c454, #d4a017)",
                                color: "#1a1a2e",
                                fontWeight: "600",
                                border: "none",
                                marginTop: "auto",
                              }}
                            >
                              {isLoading ? "Loading..." : "Submit"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Tabs */}
                <ul className="casino-report-tabs" role="tablist">
                  {TABS.map((tab) => (
                    <li key={tab.key} role="presentation">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        className={activeTab === tab.key ? "active" : ""}
                        onClick={() => handleTabChange(tab.key)}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Tab content */}
                {activeTab === "casino-pl" && renderCasinoPLTable()}
                {activeTab === "user-pl" && renderUserPLTable()}
                {activeTab === "transactions" && renderTransactionsTable()}
                {activeTab === "round-history" && renderRoundHistoryTable()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
}
